import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import { Button } from '../ui/Button'
import styles from '../../styles/components/assets/AssetsInsights.module.css'
import {
  fetchSecurityProtectionCoverage,
  securityControlInventoryUrl,
  type BuildInventoryUrlOpts,
  type SecurityProtectionCoverage,
} from '../../services/securityControlCoverageApi'
import { getActiveOrgId } from '../../utils/orgUtils'

const CONTROL_I18N: Record<string, { key: string; fallback: string }> = {
  EDR: { key: 'assets.securityControls.controls.edr', fallback: 'EDR / XDR' },
  HOST_FIREWALL: { key: 'assets.securityControls.controls.hostFirewall', fallback: 'Host Firewall' },
  DISK_ENCRYPTION: { key: 'assets.securityControls.controls.encryption', fallback: 'Encryption' },
  PATCH_MANAGEMENT: { key: 'assets.securityControls.controls.patch', fallback: 'Patch Management' },
  BACKUP_RECOVERY: { key: 'assets.securityControls.controls.backup', fallback: 'Backup' },
  SIEM_INTEGRATION: { key: 'assets.securityControls.controls.siem', fallback: 'Log / SIEM Coverage' },
}

type Props = {
  coverage?: SecurityProtectionCoverage | null
  organizationId?: string | null
  /** Bump to force refetch after asset edit */
  refreshKey?: number | string
  /**
   * Preferred when card is embedded on Assets page — updates search params in place
   * so inventory filters apply without a dead full-route remount.
   */
  onApplyInventoryFilter?: (opts: BuildInventoryUrlOpts) => void
}

const SecurityControlsCoverageCard: React.FC<Props> = ({
  coverage: coverageProp = null,
  organizationId,
  refreshKey = 0,
  onApplyInventoryFilter,
}) => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [coverage, setCoverage] = useState<SecurityProtectionCoverage | null>(coverageProp)
  const [loading, setLoading] = useState(!coverageProp)

  const hrefFor = (opts: BuildInventoryUrlOpts) => securityControlInventoryUrl(opts)

  const go = (opts: BuildInventoryUrlOpts, e?: React.MouseEvent) => {
    // Prefer in-page filter application when hosted on Assets
    if (onApplyInventoryFilter) {
      e?.preventDefault()
      onApplyInventoryFilter(opts)
      return
    }
    e?.preventDefault()
    const href = hrefFor(opts)
    const u = new URL(href, window.location.origin)
    navigate({ pathname: u.pathname, search: u.search, hash: u.hash })
    requestAnimationFrame(() => {
      document.getElementById('assets-inventory-results')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (coverageProp && !refreshKey) {
        setCoverage(coverageProp)
        setLoading(false)
        return
      }
      if (coverageProp && refreshKey) {
        setCoverage(coverageProp)
        setLoading(false)
      } else {
        setLoading(true)
      }
      try {
        const org = organizationId || getActiveOrgId()
        const dto = await fetchSecurityProtectionCoverage(token, org)
        if (cancelled) return
        if (dto && !dto.empty) {
          setCoverage(dto)
        } else if (coverageProp && !coverageProp.empty) {
          setCoverage(coverageProp)
        } else {
          setCoverage(dto || coverageProp)
        }
      } catch {
        if (!cancelled) setCoverage(coverageProp)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [coverageProp, organizationId, token, refreshKey])

  useEffect(() => {
    if (coverageProp) setCoverage(coverageProp)
  }, [coverageProp])

  const empty = !loading && (!coverage || coverage.empty)
  const summary = coverage?.summary
  const dq = coverage?.dataQuality

  const controlLabel = (controlType: string, fallbackLabel?: string) => {
    const meta = CONTROL_I18N[controlType]
    if (meta) return t(meta.key, meta.fallback)
    return fallbackLabel || controlType
  }

  const FilterLink: React.FC<{
    opts: BuildInventoryUrlOpts
    className?: string
    ariaLabel?: string
    children: React.ReactNode
  }> = ({ opts, className, ariaLabel, children }) => (
    <Link
      to={hrefFor(opts)}
      className={className}
      aria-label={ariaLabel}
      onClick={(e) => go(opts, e)}
      data-testid="security-coverage-filter-link"
    >
      {children}
    </Link>
  )

  return (
    <div className={styles.sectionCard} data-testid="security-controls-coverage-card">
      <h2 className={styles.sectionHeader}>
        <Shield className={styles.sectionIcon} aria-hidden />
        {t('assets.securityControls.title', 'Security Controls & Protection Coverage')}
      </h2>

      {loading ? (
        <p className={styles.emptyHint}>{t('common.loading', 'Loading…')}</p>
      ) : empty ? (
        <div className={styles.stackGap}>
          <p className={styles.emptyHint}>
            {t(
              'assets.securityControls.emptyTitle',
              'No security control coverage data is available yet.',
            )}
          </p>
          <p className={styles.emptyHint}>
            {t(
              'assets.securityControls.emptyHint',
              'Add control information manually or connect security integrations and agents.',
            )}
          </p>
          <div className={`${styles.divider} ${styles.footerActions}`}>
            <FilterLink opts={{ securityCoverage: 'unknown' }} className={styles.coverageCtaLink}>
              {t('assets.securityControls.viewAssets', 'View assets')}
            </FilterLink>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.lightMetricGrid}>
            <FilterLink
              opts={{ securityCoverage: 'known' }}
              className={styles.lightMetric}
              ariaLabel={t('assets.securityControls.knownCoverage', 'Known coverage')}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.securityControls.knownCoverage', 'Known coverage')}
              </p>
              <p className={styles.lightMetricValue}>
                {summary?.knownCoveragePercent != null ? `${summary.knownCoveragePercent}%` : '—'}
              </p>
            </FilterLink>
            <FilterLink
              opts={{ securityCoverage: 'verified' }}
              className={styles.lightMetric}
              ariaLabel={t('assets.securityControls.verifiedCoverage', 'Verified coverage')}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.securityControls.verifiedCoverage', 'Verified coverage')}
              </p>
              <p className={styles.lightMetricValue}>
                {summary?.verifiedCoveragePercent != null ? `${summary.verifiedCoveragePercent}%` : '0%'}
              </p>
            </FilterLink>
            <FilterLink
              opts={{ securityCoverage: 'unknown' }}
              className={styles.lightMetric}
              ariaLabel={`${t('assets.securityControls.unknownPosture', 'Unknown posture')}: ${summary?.unknownAssets ?? 0}`}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.securityControls.unknownPosture', 'Unknown posture')}
              </p>
              <p className={styles.lightMetricValue}>{summary?.unknownAssets ?? 0}</p>
            </FilterLink>
            <FilterLink
              opts={{ securityCoverage: 'gap' }}
              className={styles.lightMetric}
              ariaLabel={`${t('assets.securityControls.requiresAttention', 'Requires attention')}: ${summary?.attentionAssets ?? 0}`}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.securityControls.requiresAttention', 'Requires attention')}
              </p>
              <p className={styles.lightMetricValue}>{summary?.attentionAssets ?? 0}</p>
            </FilterLink>
          </div>

          {dq ? (
            <div className={styles.coverageDataQuality}>
              <span className={styles.coverageDataQualityLabel}>
                {t('assets.securityControls.dataQuality', 'Data quality')}
              </span>
              <FilterLink
                opts={{ verificationState: 'VERIFIED' }}
                className={styles.coverageStatLink}
              >
                {t('assets.securityControls.verified', 'Verified')}: {dq.verified}
              </FilterLink>
              {' · '}
              <FilterLink
                opts={{ verificationState: 'SELF_REPORTED' }}
                className={styles.coverageStatLink}
              >
                {t('assets.securityControls.selfReported', 'Self-reported')}: {dq.selfReported}
              </FilterLink>
              {' · '}
              <FilterLink
                opts={{ verificationState: 'UNKNOWN' }}
                className={styles.coverageStatLink}
              >
                {t('assets.securityControls.unknown', 'Unknown')}: {dq.unknown}
              </FilterLink>
              {dq.stale > 0 ? (
                <>
                  {' · '}
                  <FilterLink
                    opts={{ verificationState: 'STALE' }}
                    className={styles.coverageStatLink}
                  >
                    {t('assets.securityControls.stale', 'Stale')}: {dq.stale}
                  </FilterLink>
                </>
              ) : null}
            </div>
          ) : null}

          <div className={styles.stackGap}>
            <h3 className={styles.subsectionTitle}>
              {t('assets.securityControls.controlCoverage', 'Control coverage')}
            </h3>
            <ul className={styles.coverageList}>
              {(coverage?.controls || []).map((row) => {
                const label = controlLabel(row.controlType, row.label)
                return (
                  <li key={row.controlType} className={styles.coverageControlItem}>
                    <div className={styles.coverageControlRow}>
                      <FilterLink
                        opts={{ controlType: row.controlType }}
                        className={styles.coverageControlTitle}
                        ariaLabel={`${label} — ${row.applicableAssets} ${t('assets.securityControls.applicable', 'applicable')}`}
                      >
                        {label}
                      </FilterLink>
                      <div className={styles.coverageStatRow}>
                        <FilterLink
                          opts={{ controlType: row.controlType, controlStatus: 'ENABLED' }}
                          className={styles.coverageStatLink}
                          ariaLabel={`${label}: ${row.enabled} ${t('assets.securityControls.enabled', 'Enabled')}`}
                        >
                          <span className={styles.coverageStatCount}>{row.enabled}</span>{' '}
                          {t('assets.securityControls.enabled', 'Enabled')}
                        </FilterLink>
                        <span className={styles.coverageStatSep} aria-hidden>
                          ·
                        </span>
                        <FilterLink
                          opts={{ controlType: row.controlType, controlStatus: 'DISABLED' }}
                          className={styles.coverageStatLink}
                          ariaLabel={`${label}: ${row.disabled} ${t('assets.securityControls.disabled', 'Disabled')}`}
                        >
                          <span className={styles.coverageStatCount}>{row.disabled}</span>{' '}
                          {t('assets.securityControls.disabled', 'Disabled')}
                        </FilterLink>
                        <span className={styles.coverageStatSep} aria-hidden>
                          ·
                        </span>
                        <FilterLink
                          opts={{ controlType: row.controlType, controlStatus: 'PARTIAL' }}
                          className={styles.coverageStatLink}
                          ariaLabel={`${label}: ${row.partial} ${t('assets.securityControls.partial', 'Partial')}`}
                        >
                          <span className={styles.coverageStatCount}>{row.partial}</span>{' '}
                          {t('assets.securityControls.partial', 'Partial')}
                        </FilterLink>
                        <span className={styles.coverageStatSep} aria-hidden>
                          ·
                        </span>
                        <FilterLink
                          opts={{ controlType: row.controlType, controlStatus: 'UNKNOWN' }}
                          className={styles.coverageStatLink}
                          ariaLabel={`${label}: ${row.unknown} ${t('assets.securityControls.unknown', 'Unknown')}`}
                        >
                          <span className={styles.coverageStatCount}>{row.unknown}</span>{' '}
                          {t('assets.securityControls.unknown', 'Unknown')}
                        </FilterLink>
                      </div>
                      <div className={styles.coverageStatRow}>
                        <FilterLink
                          opts={{ controlType: row.controlType, securityCoverage: 'known' }}
                          className={styles.coverageStatLink}
                          ariaLabel={`${label}: ${t('assets.securityControls.knownCoverage', 'Known coverage')}`}
                        >
                          <span className={styles.coverageStatCount}>{row.knownAssets ?? 0}</span>{' '}
                          {t('assets.securityControls.knownCoverage', 'Known coverage')} /{' '}
                          {row.applicableAssets}
                        </FilterLink>
                        <span className={styles.coverageStatSep} aria-hidden>
                          ·
                        </span>
                        <FilterLink
                          opts={{ controlType: row.controlType, securityCoverage: 'verified' }}
                          className={styles.coverageStatLink}
                          ariaLabel={`${label}: ${t('assets.securityControls.verifiedCoverage', 'Verified coverage')}`}
                        >
                          <span className={styles.coverageStatCount}>{row.verifiedAssets ?? 0}</span>{' '}
                          {t('assets.securityControls.verifiedCoverage', 'Verified coverage')} /{' '}
                          {row.applicableAssets}
                        </FilterLink>
                        {(row.selfReported || 0) > 0 ? (
                          <>
                            <span className={styles.coverageStatSep} aria-hidden>
                              ·
                            </span>
                            <FilterLink
                              opts={{
                                controlType: row.controlType,
                                verificationState: 'SELF_REPORTED',
                              }}
                              className={styles.coverageStatLink}
                              ariaLabel={`${label}: ${row.selfReported} ${t('assets.securityControls.selfReported', 'Self-reported')}`}
                            >
                              <span className={styles.coverageStatCount}>{row.selfReported}</span>{' '}
                              {t('assets.securityControls.selfReported', 'Self-reported')}
                            </FilterLink>
                          </>
                        ) : null}
                      </div>
                      {row.applicableAssets > 0 ? (
                        <div className={styles.coverageBarTrack} aria-hidden>
                          <div
                            className={styles.coverageBarFill}
                            style={{
                              width: `${Math.min(100, row.knownCoveragePercent ?? 0)}%`,
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className={styles.stackGap} style={{ marginTop: '1rem' }}>
            <h3 className={styles.subsectionTitle}>
              {t('assets.securityControls.coverageGaps', 'Coverage gaps')}
            </h3>
            {(coverage?.topCoverageGaps || []).length === 0 ? (
              <p className={styles.emptyHint}>
                {t('assets.securityControls.noGaps', 'No coverage gaps identified.')}
              </p>
            ) : (
              <ul className={styles.coverageList}>
                {(coverage?.topCoverageGaps || []).map((gap) => (
                  <li key={gap.id}>
                    <FilterLink
                      opts={{
                        controlType: gap.controlType,
                        controlStatus: gap.controlStatus,
                      }}
                      className={styles.coverageGapBtn}
                      ariaLabel={gap.message}
                    >
                      {gap.message}
                    </FilterLink>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={`${styles.divider} ${styles.footerActions}`}>
            <Button
              type="button"
              color="outline"
              size="small"
              onClick={(e) => go({ securityCoverage: 'gap' }, e)}
            >
              {t('assets.securityControls.viewCoverageGaps', 'View coverage gaps')}
            </Button>
            <Button
              type="button"
              color="outline"
              size="small"
              className={styles.footerActionsEnd}
              onClick={(e) => go({ securityCoverage: 'all', showSecurityControls: true }, e)}
            >
              {t('assets.securityControls.viewAssetCoverage', 'View asset security coverage')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default SecurityControlsCoverageCard
